import { useCallback, useEffect, useState } from 'react';
import { fetchTripsWithPagination, type RecentTrip } from '../api/trip';

type UseAllTripsResult = {
  trips: RecentTrip[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
};

export const useAllTrips = (): UseAllTripsResult => {
  const [trips, setTrips] = useState<RecentTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchTripsWithPagination(1);
        setTrips(result.trips);
        setHasMore(result.hasMore);
        setPage(1);
      } catch {
        setError('履歴の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = await fetchTripsWithPagination(nextPage);
      setTrips(prev => [...prev, ...result.trips]);
      setHasMore(result.hasMore);
      setPage(nextPage);
    } catch {
      setError('履歴の取得に失敗しました');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page]);

  return { trips, loading, loadingMore, error, hasMore, loadMore };
};
